import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { Position, StrategyDescriptionTag } from '../services/api';
import { getPositionMetadataKey } from './positionsOrdersUtils';
import type { PositionStrategyTagService } from './positionStrategyTagService';

interface UsePositionStrategyTagsOptions {
  service: PositionStrategyTagService;
  refreshAfterDelete: () => unknown;
  refreshAfterSave: () => unknown;
}

export function usePositionStrategyTags({
  service,
  refreshAfterDelete,
  refreshAfterSave,
}: UsePositionStrategyTagsOptions) {
  const [strategyTags, setStrategyTags] = useState<StrategyDescriptionTag[]>([]);
  const [managerOpen, setManagerOpen] = useState(false);
  const [editorPosition, setEditorPosition] = useState<Position | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [strategyLabelOverride, setStrategyLabelOverride] = useState('');
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagColor, setTagColor] = useState('#14B8A6');
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  const loadStrategyTags = useCallback(async () => {
    try {
      const response = await service.listTags();
      if (response.success) {
        setStrategyTags(response.data?.tags || []);
        setTagError(null);
      } else if (managerOpen) {
        setTagError(response.error || 'Failed to load position labels');
      }
    } catch (err) {
      if (managerOpen) {
        setTagError(err instanceof Error ? err.message : 'Failed to load position labels');
      }
    }
  }, [managerOpen, service]);

  useEffect(() => {
    void loadStrategyTags();
  }, [loadStrategyTags]);

  const openManager = () => {
    setTagError(null);
    setManagerOpen(true);
  };

  const openPositionTagEditor = (position: Position) => {
    setTagError(null);
    setEditorPosition({ ...position, position_key: getPositionMetadataKey(position) });
    setSelectedTagIds((position.strategy_tags || []).map((tag) => tag.id));
    setStrategyLabelOverride(position.custom_strategy_name || '');
  };

  const openManagerFromEditor = () => {
    setEditorPosition(null);
    openManager();
  };

  const resetTagForm = () => {
    setEditingTagId(null);
    setTagName('');
    setTagDescription('');
    setTagColor('#14B8A6');
  };

  const editTag = (tag: StrategyDescriptionTag) => {
    setEditingTagId(tag.id);
    setTagName(tag.name);
    setTagDescription(tag.description || '');
    setTagColor(tag.color || '#14B8A6');
  };

  const saveStrategyTag = async (event: FormEvent) => {
    event.preventDefault();
    if (!tagName.trim()) return;
    setTagBusy(true);
    setTagError(null);
    try {
      const response = await service.saveTag({
        editingTagId,
        name: tagName,
        description: tagDescription,
        color: tagColor,
      });
      if (!response.success) {
        setTagError(response.error || 'Failed to save position label');
        return;
      }
      resetTagForm();
      await loadStrategyTags();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Failed to save position label');
    } finally {
      setTagBusy(false);
    }
  };

  const removeStrategyTag = async (tag: StrategyDescriptionTag) => {
    if (!window.confirm(`Delete the “${tag.name}” label? Existing position links will be removed.`)) return;
    setTagBusy(true);
    setTagError(null);
    try {
      const response = await service.deleteTag(tag.id);
      if (!response.success) {
        setTagError(response.error || 'Failed to delete position label');
        return;
      }
      if (editingTagId === tag.id) resetTagForm();
      setSelectedTagIds((ids) => ids.filter((id) => id !== tag.id));
      await loadStrategyTags();
      await refreshAfterDelete();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Failed to delete position label');
    } finally {
      setTagBusy(false);
    }
  };

  const savePositionTags = async () => {
    if (!editorPosition) return;
    const positionKey = getPositionMetadataKey(editorPosition);
    setTagBusy(true);
    setTagError(null);
    try {
      const response = await service.savePositionMetadata(
        positionKey,
        strategyLabelOverride,
        selectedTagIds,
      );
      if (!response.success) {
        setTagError(response.error || 'Failed to update position labels');
        return;
      }
      setEditorPosition(null);
      await refreshAfterSave();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Failed to update position labels');
    } finally {
      setTagBusy(false);
    }
  };

  return {
    strategyTags,
    managerOpen,
    setManagerOpen,
    editorPosition,
    setEditorPosition,
    selectedTagIds,
    setSelectedTagIds,
    strategyLabelOverride,
    setStrategyLabelOverride,
    editingTagId,
    tagName,
    setTagName,
    tagDescription,
    setTagDescription,
    tagColor,
    setTagColor,
    tagBusy,
    tagError,
    setTagError,
    openManager,
    openPositionTagEditor,
    openManagerFromEditor,
    resetTagForm,
    editTag,
    saveStrategyTag,
    removeStrategyTag,
    savePositionTags,
  };
}
